import { registerLib, isCodeRegistered, registerCodes, createScopedErrors } from '@norkron/web-errors';
import { z } from 'zod';

// src/error-codes.ts
var WEB_THEMER_LIB_ID = "@norkron/web-themer";
var THEMER_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.themer");
try {
  registerLib(WEB_THEMER_LIB_ID, THEMER_TOKEN);
} catch {
}
if (!isCodeRegistered(WEB_THEMER_LIB_ID, "THEMER_DTF_PARSE_FAIL")) {
  try {
    registerCodes(WEB_THEMER_LIB_ID, {
      THEMER_DTF_PARSE_FAIL: {
        description: "Invalid W3C DTF input could not be mapped to BrandingConfig.",
        severity: "error",
        docsAnchor: "themer/dtf-parse",
        exampleFix: "Ensure the DTF document has the expected $extensions.norkron.brandingConfig metadata block."
      },
      THEMER_MISSING_CSS_VAR: {
        description: "A requested CSS custom property was not defined on document.documentElement. The fallback (or empty string) was returned.",
        severity: "warning",
        docsAnchor: "themer/missing-css-var",
        exampleFix: "Add the CSS variable to your theme stylesheet (e.g. :root { --my-var: ...; }) or pass an explicit fallback to getCssVar()."
      },
      WEB_THEMER_INVALID_INPUT: {
        description: "A public input to @norkron/web-themer failed zod validation (Wave 10 schema mandate). Inspect payload.fieldPath + payload.expected; pass input matching the schema exported by this lib.",
        severity: "warning",
        docsAnchor: "https://norkron.dev/web-errors/WEB_INVALID_INPUT_SHAPE"
      },
      TH_VAR_MISSING: {
        description: "selfCheck finding: a CSS variable was requested at least once via getCssVar() during the session but was never defined on :root (or any [data-theme] scope). The fallback (or empty string) was returned for every call.",
        severity: "warning",
        docsAnchor: "themer/var-missing",
        exampleFix: "Define the variable on :root (or a [data-theme] selector) in your theme CSS, or pass an explicit fallback to getCssVar()."
      },
      TH_VAR_FALLBACK_USED: {
        description: "selfCheck finding: a CSS variable resolved via the caller-supplied fallback to getCssVar() at least once during the session. The variable is partially defined or the call ran before the theme stylesheet loaded.",
        severity: "info",
        docsAnchor: "themer/var-fallback-used",
        exampleFix: "Define the variable explicitly in your theme CSS to avoid relying on the caller-supplied fallback."
      },
      TH_COVERAGE_REPORT: {
        description: "selfCheck always-emitted info finding: session totals of getCssVar() calls \u2014 { uniqueRequested, uniqueSatisfied, uniqueFallbackUsed, totalCalls }.",
        severity: "info",
        docsAnchor: "themer/coverage-report"
      },
      TH_SELFCHECK_BUDGET_EXCEEDED: {
        description: "selfCheck info finding: the time budget (default budgetMs) was exceeded while iterating session token stats. Partial findings were returned.",
        severity: "info",
        docsAnchor: "themer/selfcheck-budget-exceeded",
        exampleFix: "Raise budgetMs in the SelfCheckOptions passed to selfCheck(), or reduce the number of unique CSS variables requested per session."
      },
      TH_SELFCHECK_INTERNAL_ERROR: {
        description: "selfCheck info finding: the self-check threw internally and was caught. Reported with severity info so callers can detect failures without halting the conformance run.",
        severity: "info",
        docsAnchor: "themer/selfcheck-internal-error"
      },
      THEMER_ACCENT_CONTRAST_FAIL: {
        description: 'defineAccentScale() measured an accent stop against BOTH candidate text tokens and neither reached the configured floor. The on-accent pair for that hue cannot be made accessible without changing the stop \u2014 that is a design decision, not a value to round down silently. Emitted whenever a cell fails, including under onFail:"report" where the scale is still returned.',
        severity: "error",
        docsAnchor: "themer/accent-contrast-fail",
        exampleFix: "Inspect scale.failures[] \u2014 each entry carries the measured ratio, the required ratio, the APCA Lc, and a computed suggestion naming the nearest same-hue stop that would pass. Adopt the suggestion, choose a different hue, or (only when the accent genuinely carries large / non-text UI) set largeText: true."
      },
      THEMER_BRANDING_INVALID: {
        description: "BrandingConfig failed Zod validation. The supplied config does not satisfy the brand contract \u2014 audit libs cannot trust it.",
        severity: "error",
        docsAnchor: "themer/branding-invalid",
        exampleFix: "Inspect the issues[] array on the thrown error. Each entry has a path (dot-joined) and a Zod message describing the failure."
      }
    });
  } catch {
  }
}
var { warn, panic, reportInvalidInput } = createScopedErrors(
  WEB_THEMER_LIB_ID,
  THEMER_TOKEN
);

// src/css-vars.ts
var warnedVariables = /* @__PURE__ */ new Set();
var tokenStats = /* @__PURE__ */ new Map();
function recordTokenAccess(varName, satisfied, usedFallback) {
  const prev = tokenStats.get(varName);
  if (prev) {
    prev.requested += 1;
    if (usedFallback) prev.fallbackUsed += 1;
    if (satisfied) prev.satisfied = true;
  } else {
    tokenStats.set(varName, {
      requested: 1,
      fallbackUsed: usedFallback ? 1 : 0,
      satisfied
    });
  }
}
function __getTokenStats() {
  return tokenStats;
}
function __getActivePalette() {
  const out = /* @__PURE__ */ new Map();
  if (typeof document === "undefined") return out;
  const root = document.documentElement;
  if (!root) return out;
  const cs = getComputedStyle(root);
  const len = cs.length ?? 0;
  for (let i = 0; i < len; i++) {
    const name = cs.item(i);
    if (!name) continue;
    if (!name.startsWith("--")) continue;
    if (!/(color|bg|fg|text|surface|border|series|brand|accent|tone|palette)/i.test(name)) {
      continue;
    }
    const value = cs.getPropertyValue(name).trim().replaceAll(/"/g, "");
    if (value.length === 0) continue;
    out.set(name, value);
  }
  return out;
}
function __resetTokenStats() {
  tokenStats.clear();
}
function getCssVar(name, fallback) {
  if (name === void 0 || name === null) {
    throw new Error(
      `[themer] getCssVar called with ${String(name)} key.
  \u2192 Check that the variable name is correctly defined.`
    );
  }
  if (typeof name !== "string") {
    const received = typeof name === "symbol" ? "symbol" : typeof name === "bigint" ? "bigint" : Array.isArray(name) ? "array" : typeof name;
    throw new Error(
      `[themer] getCssVar called with non-string key (received ${received}).
  \u2192 CSS variable names must be strings (e.g. '--my-var' or 'my-var').`
    );
  }
  if (typeof document === "undefined") {
    return fallback ?? "";
  }
  const varName = name.startsWith("--") ? name : `--${name}`;
  const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim().replaceAll(/"/g, "");
  const satisfied = value.length > 0;
  const usedFallback = !satisfied && fallback !== void 0;
  recordTokenAccess(varName, satisfied, usedFallback);
  if (!value) {
    if (!warnedVariables.has(varName)) {
      warnedVariables.add(varName);
      try {
        warn({
          fn: "getCssVar",
          code: "THEMER_MISSING_CSS_VAR",
          message: `Missing CSS variable: ${varName}` + (fallback ? ` (using fallback: "${fallback}")` : "") + `. Add this variable to your theme CSS.`,
          prop: varName,
          expected: "a CSS custom property defined on :root or document.documentElement",
          dedupKey: varName
        });
      } catch {
      }
      if (typeof console !== "undefined") {
        console.warn(
          `[themer] Missing CSS variable: ${varName}` + (fallback ? ` (using fallback: "${fallback}")` : "") + `
  \u2192 Add this variable to your theme CSS.`
        );
      }
    }
    return fallback ?? "";
  }
  return value;
}
function getCssVars(names) {
  const result = {};
  for (const name of names) {
    result[name] = getCssVar(name);
  }
  return result;
}
function resetCssVarWarnings() {
  warnedVariables.clear();
}
function defineThemeReader(map) {
  const keys = Object.keys(map);
  return () => {
    const out = {};
    for (const key of keys) {
      const cssName = map[key];
      out[key] = getCssVar(cssName);
    }
    return out;
  };
}
var SelfCheckOptionsSchema = z.object({
  budgetMs: z.number().int().positive().max(5e3).optional(),
  minSeverity: z.enum(["critical", "major", "minor", "info"]).optional(),
  locale: z.string().min(2).max(35).optional()
}).strict();

// src/dtf-import.ts
function colorToken(value, description) {
  const t = { $value: value, $type: "color" };
  if (description !== void 0) t.$description = description;
  return t;
}
function toDtf(config) {
  const doc = {
    $extensions: {
      norkron: {
        brandingConfig: {
          id: config.id,
          version: config.version,
          aestheticDirection: config.aestheticDirection,
          contrastTargets: config.colors.contrastTargets,
          typography: config.typography,
          motion: config.motion,
          breakpoints: config.breakpoints,
          a11y: config.a11y,
          radii: config.radii,
          spacing: config.spacing
        }
      }
    },
    color: {},
    surface: {},
    text: {},
    state: {},
    chart: {}
  };
  for (const [k, tok] of Object.entries(config.colors.palette)) {
    doc.color[k] = colorToken(tok.value, tok.description);
  }
  for (const [k, tok] of Object.entries(config.colors.surfaces)) {
    doc.surface[k] = colorToken(tok.value, tok.description);
  }
  for (const [k, tok] of Object.entries(config.colors.text)) {
    doc.text[k] = colorToken(tok.value, tok.description);
  }
  for (const [k, tok] of Object.entries(config.colors.state)) {
    doc.state[k] = colorToken(tok.value, tok.description);
  }
  config.colors.chart.forEach((tok, i) => {
    doc.chart[`c${i + 1}`] = colorToken(
      tok.value,
      tok.description
    );
  });
  return doc;
}
function readColorGroup(group) {
  const out = {};
  if (group === void 0) return out;
  for (const [k, v] of Object.entries(group)) {
    if (v && typeof v === "object" && "$value" in v) {
      const tok = v;
      if (typeof tok.$value !== "string") {
        panic({
          fn: "fromDtf",
          code: "THEMER_DTF_PARSE_FAIL",
          kind: "contract",
          message: `Token "${k}" $value is not a string`,
          received: tok.$value
        });
      }
      const entry = {
        value: tok.$value
      };
      if (tok.$description !== void 0) entry.description = tok.$description;
      out[k] = entry;
    }
  }
  return out;
}
function fromDtf(doc) {
  const ext = doc.$extensions?.norkron?.brandingConfig;
  if (!ext) {
    panic({
      fn: "fromDtf",
      code: "THEMER_DTF_PARSE_FAIL",
      kind: "contract",
      message: "DTF document missing $extensions.norkron.brandingConfig metadata",
      received: doc.$extensions
    });
    throw new Error(
      "DTF document missing $extensions.norkron.brandingConfig metadata"
    );
  }
  const palette = readColorGroup(doc.color);
  const surfacesRaw = readColorGroup(doc.surface);
  const textRaw = readColorGroup(doc.text);
  const stateRaw = readColorGroup(doc.state);
  const chartRaw = readColorGroup(doc.chart);
  const chart = Object.keys(chartRaw).sort((a, b) => {
    const ai = parseInt(a.replace(/\D/g, ""), 10) || 0;
    const bi = parseInt(b.replace(/\D/g, ""), 10) || 0;
    return ai - bi;
  }).map((k) => chartRaw[k]).filter((v) => v !== void 0);
  const config = {
    id: ext.id,
    version: ext.version,
    aestheticDirection: ext.aestheticDirection,
    colors: {
      palette,
      surfaces: surfacesRaw,
      text: textRaw,
      state: stateRaw,
      chart,
      contrastTargets: ext.contrastTargets
    },
    typography: ext.typography,
    spacing: ext.spacing,
    radii: ext.radii,
    motion: ext.motion,
    breakpoints: ext.breakpoints,
    a11y: ext.a11y
  };
  return config;
}
var colorTokenSchema = z.object({
  value: z.string().min(1),
  description: z.string().optional()
});
var fontStackSchema = z.object({
  stack: z.array(z.string().min(1)).min(1),
  display: z.enum(["auto", "block", "swap", "fallback", "optional"]).optional()
});
var surfacesSchema = z.object({
  base: colorTokenSchema,
  elevated: colorTokenSchema,
  sunken: colorTokenSchema,
  overlay: colorTokenSchema,
  inverse: colorTokenSchema
});
var textSchema = z.object({
  primary: colorTokenSchema,
  secondary: colorTokenSchema,
  muted: colorTokenSchema,
  disabled: colorTokenSchema,
  inverse: colorTokenSchema,
  link: colorTokenSchema
});
var stateSchema = z.object({
  info: colorTokenSchema,
  success: colorTokenSchema,
  warn: colorTokenSchema,
  danger: colorTokenSchema,
  neutral: colorTokenSchema
});
var contrastSchema = z.object({
  bodyAPCA: z.number().min(0).max(108),
  uiAPCA: z.number().min(0).max(108),
  enforce: z.enum(["WCAG", "APCA", "both"])
});
var colorsSchema = z.object({
  palette: z.record(z.string(), colorTokenSchema),
  surfaces: surfacesSchema,
  text: textSchema,
  state: stateSchema,
  chart: z.array(colorTokenSchema).min(1),
  contrastTargets: contrastSchema
});
var typographySchema = z.object({
  families: z.object({
    sans: fontStackSchema,
    serif: fontStackSchema,
    mono: fontStackSchema,
    display: fontStackSchema
  }),
  scale: z.array(z.number().positive()).min(3),
  lineHeights: z.object({
    tight: z.number().positive(),
    normal: z.number().positive(),
    loose: z.number().positive()
  }),
  weights: z.record(z.string(), z.number().int().min(1).max(1e3)),
  letterSpacing: z.record(z.string(), z.string()),
  readingLineLength: z.object({
    minCh: z.number().positive(),
    maxCh: z.number().positive(),
    targetCh: z.number().positive()
  }),
  capsize: z.object({
    enabled: z.boolean(),
    capHeightTrim: z.union([z.literal("auto"), z.number()])
  })
});
var motionSchema = z.object({
  durations: z.object({
    instant: z.number().min(0),
    fast: z.number().positive(),
    base: z.number().positive(),
    slow: z.number().positive(),
    slowest: z.number().positive()
  }),
  easings: z.record(z.string(), z.string().min(1)),
  reducedMotionStrategy: z.enum(["remove", "shorten", "replace-with-fade"])
});
var breakpointsSchema = z.object({
  sm: z.number().positive(),
  md: z.number().positive(),
  lg: z.number().positive(),
  xl: z.number().positive(),
  "2xl": z.number().positive(),
  container: z.record(z.string(), z.number().positive())
});
var a11ySchema = z.object({
  focusRingWidthPx: z.number().min(1),
  focusRingOffsetPx: z.number().min(0),
  minTouchTargetPx: z.number().min(24),
  forcedColors: z.object({
    mode: z.enum(["preserve", "override"]),
    tokenMap: z.record(z.string(), z.string())
  }),
  prefersReducedTransparency: z.enum(["preserve", "opaque"])
});
var brandingConfigSchema = z.object({
  id: z.string().min(1),
  version: z.string().regex(/^\d+\.\d+\.\d+([-+].*)?$/, "semver"),
  aestheticDirection: z.enum([
    "editorial",
    "brutalist",
    "dense-data",
    "playful-native",
    "premium-restrained",
    "geological"
  ]),
  colors: colorsSchema,
  typography: typographySchema,
  spacing: z.array(z.number().min(0)).min(3),
  radii: z.object({
    none: z.string(),
    sm: z.string(),
    md: z.string(),
    lg: z.string(),
    pill: z.string()
  }),
  motion: motionSchema,
  breakpoints: breakpointsSchema,
  a11y: a11ySchema
});
function validateBrandingConfig(candidate) {
  const parsed = brandingConfigSchema.safeParse(candidate);
  if (parsed.success) {
    return { valid: true, issues: [] };
  }
  const issues = parsed.error.issues.map(
    (i) => ({
      path: i.path.map((p) => String(p)).join("."),
      message: i.message,
      code: i.code
    })
  );
  return { valid: false, issues };
}
function assertBrandingConfig(candidate) {
  const result = validateBrandingConfig(candidate);
  if (!result.valid) {
    panic({
      fn: "assertBrandingConfig",
      code: "THEMER_BRANDING_INVALID",
      kind: "contract",
      message: `BrandingConfig failed validation (${result.issues.length} issue${result.issues.length === 1 ? "" : "s"})`,
      received: { issues: result.issues }
    });
  }
}
var BrandingConfigSchema = brandingConfigSchema;

// src/zod-bridge.ts
function toZodLikeSafeParseError(failure) {
  return failure;
}
var DeepBrandingConfigSchema = brandingConfigSchema;
var NameSchema = z.string().min(1);
z.object({
  token: NameSchema,
  fallback: z.string().optional()
});
z.array(NameSchema);
z.undefined();
z.object({}).passthrough();
var ThemePreferenceSchema = z.string().min(1);
z.enum(["light", "dark"]);
z.object({}).passthrough();
z.object({}).passthrough();
z.undefined();
var SetThemeInputSchema = z.string().min(1);
z.undefined();
var SubscribeThemeInputSchema = z.unknown().refine(
  (v) => typeof v === "function",
  { message: "expected function" }
);
z.undefined();
var ResolvePreferenceInputSchema = ThemePreferenceSchema;
var InitThemerInputSchema = z.object({}).passthrough();
z.object({}).passthrough().optional();
var BrandingConfigSchema2 = DeepBrandingConfigSchema;
z.string();
z.enum([
  "editorial",
  "brutalist",
  "dense-data",
  "playful-native",
  "premium-restrained",
  "geological"
]);
z.object({
  value: z.string().min(1),
  description: z.string().optional()
});
z.enum([
  "base",
  "elevated",
  "sunken",
  "overlay",
  "inverse"
]);
z.enum([
  "primary",
  "secondary",
  "muted",
  "disabled",
  "inverse",
  "link"
]);
z.enum([
  "info",
  "success",
  "warn",
  "danger",
  "neutral"
]);
z.object({
  stack: z.array(z.string().min(1)).min(1),
  display: z.enum(["auto", "block", "swap", "fallback", "optional"]).optional()
});
z.object({
  bodyAPCA: z.number().min(0).max(108),
  uiAPCA: z.number().min(0).max(108),
  enforce: z.enum(["WCAG", "APCA", "both"])
});
z.object({}).passthrough();
z.unknown();
z.unknown();
var BrandingConfigValidationIssueSchema = z.object({
  path: z.string(),
  message: z.string(),
  code: z.string()
});
z.object({
  valid: z.boolean(),
  issues: z.array(BrandingConfigValidationIssueSchema)
});
z.object({}).passthrough();
var CssEmitOptionsSchema = z.object({}).passthrough();
z.object({
  config: BrandingConfigSchema2,
  options: CssEmitOptionsSchema.optional()
});
var PresetSchema = BrandingConfigSchema2;
z.record(z.string(), PresetSchema);
var LearnOptionsSchema = z.object({}).passthrough();
z.object({}).passthrough();
z.object({
  sample: z.unknown(),
  options: LearnOptionsSchema.optional()
}).refine((v) => "sample" in v, { message: "sample is required", path: ["sample"] });
var HexColorSchema = z.string().trim().regex(/^#?(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "expected a 3- or 6-digit hex colour");
var AccentThemeSchema = z.enum(["light", "dark"]);
var AccentGradeSchema = z.enum(["AAA", "AA", "fail"]);
var AccentEnforceSchema = z.enum(["WCAG", "APCA", "both"]);
var AccentStopSchema = z.object({
  light: HexColorSchema,
  dark: HexColorSchema
});
var AccentScaleInputSchema = z.record(z.string().min(1), AccentStopSchema).refine((v) => Object.keys(v).length > 0, { message: "define at least one hue" });
var AccentTextTokensSchema = z.object({
  light: HexColorSchema,
  dark: HexColorSchema
});
var AccentScaleOptionsSchema = z.object({
  minGrade: z.enum(["AA", "AAA"]).optional(),
  largeText: z.boolean().optional(),
  textTokens: AccentTextTokensSchema.partial().optional(),
  enforce: AccentEnforceSchema.optional(),
  minApcaLc: z.number().min(0).max(110).optional(),
  onFail: z.enum(["throw", "report"]).optional()
});
var DefineAccentScaleInputSchema = z.object({
  input: AccentScaleInputSchema,
  opts: AccentScaleOptionsSchema.optional()
});
var AccentStopSuggestionSchema = z.object({
  hex: HexColorSchema,
  deltaL: z.number(),
  ratio: z.number().min(1).max(21),
  on: HexColorSchema
});
z.object({
  name: z.string().min(1),
  theme: AccentThemeSchema,
  stop: HexColorSchema,
  on: HexColorSchema,
  ratio: z.number().min(1).max(21),
  required: z.number().min(1).max(21),
  apca: z.number(),
  requiredApca: z.number(),
  failedUnder: z.array(z.enum(["WCAG", "APCA"])),
  suggestion: AccentStopSuggestionSchema.optional()
});
z.object({
  name: z.string().min(1),
  theme: AccentThemeSchema,
  stop: HexColorSchema,
  on: HexColorSchema,
  ratio: z.number().min(1).max(21),
  grade: AccentGradeSchema,
  wcagGradeRaw: z.string().min(1),
  apca: z.number(),
  apcaPass: z.boolean(),
  passes: z.boolean(),
  required: z.number().min(1).max(21),
  polarity: z.enum(["light-on-dark", "dark-on-light"]),
  oklch: z.tuple([z.number(), z.number(), z.number()]),
  runnerUp: z.object({ on: HexColorSchema, ratio: z.number().min(1).max(21) })
});
z.object({
  attribute: z.string().min(1).optional(),
  themeAttribute: z.string().min(1).optional(),
  indent: z.string().optional(),
  trailingNewline: z.boolean().optional()
});
z.object({
  names: z.array(z.string().min(1)),
  tokenNames: z.array(z.string().startsWith("--norkron-")),
  minGrade: z.enum(["AA", "AAA"]),
  largeText: z.boolean(),
  enforce: AccentEnforceSchema,
  requiredRatio: z.number().min(1).max(21),
  requiredApca: z.number(),
  textTokens: AccentTextTokensSchema,
  cellCount: z.number().int().min(2),
  worst: z.object({
    name: z.string().min(1),
    theme: AccentThemeSchema,
    ratio: z.number().min(1).max(21),
    grade: AccentGradeSchema
  }),
  failing: z.array(
    z.object({ name: z.string().min(1), theme: AccentThemeSchema, ratio: z.number() })
  )
});
z.object({
  vars: z.function(),
  names: z.function(),
  audit: z.function(),
  stop: z.function(),
  on: z.function(),
  has: z.function(),
  toCss: z.function(),
  describe: z.function(),
  failures: z.array(z.unknown())
});
var AccentVarsInputSchema = z.object({
  name: z.string().min(1),
  theme: AccentThemeSchema
});
var ApplyAccentInputSchema = z.object({
  name: z.string().min(1),
  theme: AccentThemeSchema
});
var AssertAccentScaleGradeInputSchema = z.object({
  minGrade: z.enum(["AA", "AAA"])
});
z.array(z.string().startsWith("--norkron-")).min(1).readonly();
var BreakpointSchema = z.enum(["xs", "sm", "md", "lg", "xl", "2xl", "3xl"]);
z.object({
  xs: z.number().int().min(0),
  sm: z.number().int().min(0),
  md: z.number().int().min(0),
  lg: z.number().int().min(0),
  xl: z.number().int().min(0),
  "2xl": z.number().int().min(0),
  "3xl": z.number().int().min(0)
});
var CanonicalBreakpointPxSchema = z.union([
  z.literal(0),
  z.literal(480),
  z.literal(768),
  z.literal(1024),
  z.literal(1280),
  z.literal(1536),
  z.literal(1920)
]);
z.array(BreakpointSchema).length(7);
z.array(CanonicalBreakpointPxSchema).length(7);
z.object({
  viewport: z.number().finite().min(0),
  bp: BreakpointSchema
});
z.number().finite().min(0).optional();
z.number().finite();
z.number().finite();
var CanonicalVariantIdSchema = z.enum([
  "minimalisticCompact",
  "modernAverage",
  "spaciousPremiumArtistic"
]);
var CanonicalColorModeSchema = z.enum(["light", "dark"]);
z.array(CanonicalVariantIdSchema).length(3);
z.object({
  variant: CanonicalVariantIdSchema,
  mode: CanonicalColorModeSchema
});
z.undefined();
z.object({
  a: z.union([z.string().min(3), z.object({ r: z.number(), g: z.number(), b: z.number() })]),
  b: z.union([z.string().min(3), z.object({ r: z.number(), g: z.number(), b: z.number() })])
});
z.undefined();
z.undefined();
z.undefined();

// src/validate-input.ts
function validateInput(schema, fn, input) {
  const parsed = schema.safeParse(input);
  if (!parsed.success) {
    reportInvalidInput({
      lib: "@norkron/web-themer",
      libToken: THEMER_TOKEN,
      fn,
      parseResult: toZodLikeSafeParseError(parsed)
    });
  }
}

// src/theme-store.ts
var DEFAULT_STORAGE_KEY = "norkron-theme";
var DEFAULT_ATTRIBUTE = "data-theme";
var DEFAULT_PREFERENCE = "auto";
var listeners = /* @__PURE__ */ new Set();
var warnedListeners = /* @__PURE__ */ new WeakSet();
function isProduction() {
  try {
    return typeof process !== "undefined" && process.env != null && process.env.NODE_ENV === "production";
  } catch {
    return false;
  }
}
function broadcastListeners(theme) {
  const snapshot = Array.from(listeners);
  const prod = isProduction();
  for (const fn of snapshot) {
    if (!listeners.has(fn)) continue;
    try {
      fn(theme);
    } catch (err) {
      if (!prod && !warnedListeners.has(fn)) {
        warnedListeners.add(fn);
        if (typeof console !== "undefined") {
          console.warn(
            "[themer] subscriber threw during theme broadcast \u2014 other subscribers were still notified. This warning fires once per subscriber.",
            err
          );
        }
      }
    }
  }
}
function safeRead(storageKey) {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}
function safeWrite(storageKey, value) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey, value);
  } catch {
  }
}
function osPrefersDark() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}
function resolvePreference(preference, prefersDark = osPrefersDark()) {
  validateInput(ResolvePreferenceInputSchema, "resolvePreference", preference);
  if (preference === "auto") return prefersDark ? "dark" : "light";
  return preference;
}
function getTheme(opts = {}) {
  const attribute = opts.attribute ?? DEFAULT_ATTRIBUTE;
  const storageKey = opts.storageKey ?? DEFAULT_STORAGE_KEY;
  if (typeof document !== "undefined") {
    const attr = document.documentElement.getAttribute(attribute);
    if (attr) return attr;
  }
  const stored = safeRead(storageKey);
  if (stored) return resolvePreference(stored);
  return resolvePreference(DEFAULT_PREFERENCE);
}
function setTheme(theme, opts = {}) {
  validateInput(SetThemeInputSchema, "setTheme", theme);
  const {
    persist = true,
    broadcast = true,
    attribute = DEFAULT_ATTRIBUTE,
    storageKey = DEFAULT_STORAGE_KEY
  } = opts;
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute(attribute, theme);
  }
  if (persist) {
    safeWrite(storageKey, theme);
  }
  if (broadcast) {
    broadcastListeners(theme);
  }
}
function isDarkMode(attribute = DEFAULT_ATTRIBUTE) {
  if (typeof document === "undefined") return false;
  return document.documentElement.getAttribute(attribute) === "dark";
}
function subscribeTheme(cb) {
  validateInput(SubscribeThemeInputSchema, "subscribeTheme", cb);
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
function initThemer(opts = {}) {
  validateInput(InitThemerInputSchema, "initThemer", opts);
  const {
    storageKey = DEFAULT_STORAGE_KEY,
    attribute = DEFAULT_ATTRIBUTE,
    defaultPreference = DEFAULT_PREFERENCE,
    themes,
    applyToDocument = true,
    broadcast = true
  } = opts;
  const isAccepted = (p) => {
    if (p === "auto") return true;
    if (!themes || themes.length === 0) return true;
    return themes.includes(p);
  };
  let preference = (() => {
    const stored = safeRead(storageKey);
    if (stored && isAccepted(stored)) return stored;
    return defaultPreference;
  })();
  let prefersDark = osPrefersDark();
  let resolved = resolvePreference(preference, prefersDark);
  const apply = () => {
    if (applyToDocument && typeof document !== "undefined") {
      document.documentElement.setAttribute(attribute, resolved);
    }
    broadcastListeners(resolved);
  };
  apply();
  const cleanup = [];
  if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => {
      prefersDark = e.matches;
      if (preference === "auto") {
        resolved = resolvePreference(preference, prefersDark);
        apply();
      }
    };
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
      cleanup.push(() => mql.removeEventListener("change", onChange));
    } else if (typeof mql.addListener === "function") {
      mql.addListener(onChange);
      cleanup.push(() => mql.removeListener(onChange));
    }
  }
  if (broadcast && typeof window !== "undefined") {
    const onStorage = (e) => {
      if (e.key !== storageKey) return;
      const next = e.newValue;
      if (!next || !isAccepted(next)) return;
      preference = next;
      resolved = resolvePreference(preference, prefersDark);
      apply();
    };
    window.addEventListener("storage", onStorage);
    cleanup.push(() => window.removeEventListener("storage", onStorage));
  }
  const instance = {
    get preference() {
      return preference;
    },
    get resolved() {
      return resolved;
    },
    setPreference(p) {
      if (!isAccepted(p)) return;
      preference = p;
      resolved = resolvePreference(preference, prefersDark);
      safeWrite(storageKey, p);
      apply();
    },
    destroy() {
      for (const fn of cleanup) fn();
      cleanup.length = 0;
    }
  };
  return instance;
}

export { AccentVarsInputSchema, ApplyAccentInputSchema, AssertAccentScaleGradeInputSchema, BrandingConfigSchema, DefineAccentScaleInputSchema, SelfCheckOptionsSchema, __getActivePalette, __getTokenStats, __resetTokenStats, assertBrandingConfig, brandingConfigSchema, defineThemeReader, fromDtf, getCssVar, getCssVars, getTheme, initThemer, isDarkMode, osPrefersDark, resetCssVarWarnings, resolvePreference, setTheme, subscribeTheme, toDtf, validateBrandingConfig, validateInput, warn };
//# sourceMappingURL=chunk-CDSUSZHW.js.map
//# sourceMappingURL=chunk-CDSUSZHW.js.map